import { useMemo, useState, type ReactNode } from 'react'
import { MapPin } from '@phosphor-icons/react'
import { useActor } from '@/auth/pin/phoneSession'
import { useFormat, useI18n } from '@/i18n'
import { newId } from '@/lib/ids'
import { enqueueCommand } from '@/offline'
import {
  Badge,
  Banner,
  BilingualLabel,
  Button,
  Card,
  CardBody,
  CardHeader,
  Field,
  ScanField,
  useToast,
} from '@/ui'
import type { CountBayPayload } from '../commands'
import '../commands'
import { bagsAt, placePath, type StoreState } from '../projection'
import { classifyScan, compareCount } from '../rules'
import { Counter, ScanButton } from './bits'
import { useRefusalText } from './useRefusalText'
import { useDraftState } from '../useStore'

interface CountDraft {
  count_id: string
  location_id: string | null
  started_at: string | null
  codes: string[]
}

const emptyCount = (locationId: string | null): CountDraft => ({
  count_id: newId(),
  location_id: locationId,
  started_at: null,
  codes: [],
})

/**
 * F5.6 Count a place (also the first step of F5-B1.a): scan the place label, then every tag there;
 * the phone compares with the bags it expects at the place and below, and sends one count_bay.
 */
export function CountFlow({
  state,
  initialLocationId,
  onCounted,
  doneLabel,
}: {
  state: StoreState
  initialLocationId?: string | null
  /** Called after count_bay is queued. */
  onCounted: (r: { count_id: string; location_id: string; command_id: string }) => void
  doneLabel?: ReactNode
}) {
  const { t } = useI18n()
  const fmt = useFormat()
  const toast = useToast()
  const actor = useActor()
  const refusal = useRefusalText()
  const [draft, update, loaded, clear] = useDraftState<CountDraft>(
    `store.count:${state.siteId}`,
    'store.count',
    () => emptyCount(initialLocationId ?? null),
    actor.user_id,
  )
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const location = draft.location_id ? (state.locations.get(draft.location_id) ?? null) : null
  const expected = useMemo(() => (location ? bagsAt(state, location.id) : []), [state, location])
  const cmp = useMemo(
    () => compareCount(state, expected, draft.codes),
    [state, expected, draft.codes],
  )
  const requests = state.cache.count_requests.filter((r) => !state.counts.has(r.location_id))

  const setPlace = (code: string | null) => {
    setError(null)
    if (!code) return update((d) => ({ ...d, location_id: null, codes: [], started_at: null }))
    const k = classifyScan(state, code)
    if (k.kind !== 'LOCATION') return setError(refusal('TAG_WRONG_KIND'))
    if (!k.location.active) return setError(refusal('LOCATION_INACTIVE'))
    update((d) => ({
      ...d,
      location_id: k.location.id,
      codes: [],
      started_at: new Date().toISOString(),
    }))
  }

  const done = async () => {
    if (!location) return
    setSaving(true)
    try {
      const payload: CountBayPayload = {
        count: {
          id: draft.count_id,
          location_id: location.id,
          qr_payloads: draft.codes,
          started_at: draft.started_at ?? new Date().toISOString(),
          ended_at: new Date().toISOString(),
        },
      }
      const parents = [
        ...new Set(expected.map((b) => b.pending_command_id).filter((x): x is string => !!x)),
      ]
      const rec = await enqueueCommand({
        type: 'count_bay',
        payload,
        actorId: actor.user_id,
        parentIds: parents,
      })
      const result = { count_id: draft.count_id, location_id: location.id, command_id: rec.id }
      await clear()
      toast.success(t(cmp.matches ? 'store.count.matched' : 'store.count.mismatch'))
      onCounted(result)
    } catch (e) {
      toast.error(t('store.saveFailed'), e instanceof Error ? e.message : undefined)
    } finally {
      setSaving(false)
    }
  }

  if (!loaded) return null
  return (
    <div className="flex flex-col gap-4">
      {!location && requests.length > 0 && (
        <Card>
          <CardHeader eyebrow={t('store.count.requests', { count: requests.length })} />
          <CardBody className="py-1">
            <ul>
              {requests.map((r) => (
                <li key={r.id}>
                  <button
                    type="button"
                    className="flex min-h-touch w-full items-center justify-between gap-2 border-b border-rule/70 py-2 text-left last:border-b-0"
                    onClick={() =>
                      update((d) => ({
                        ...d,
                        location_id: r.location_id,
                        started_at: new Date().toISOString(),
                      }))
                    }
                  >
                    <span>
                      <span className="block font-mono">
                        {state.locations.get(r.location_id)?.code}
                      </span>
                      {r.note && <span className="block text-[12px] text-muted">{r.note}</span>}
                    </span>
                    <span className="text-[12px] text-muted">{fmt.relative(r.requested_at)}</span>
                  </button>
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      )}
      <Card>
        <CardBody className="flex flex-col gap-3">
          <Field
            label={<BilingualLabel k="store.place.label" />}
            error={error ?? undefined}
            required
          >
            <ScanField
              value={location?.code ?? null}
              onChange={(c) => setPlace(c)}
              disabled={draft.codes.length > 0}
            />
          </Field>
          {location && (
            <div className="flex items-center gap-2 text-[14px]">
              <MapPin className="text-amber-dark" aria-hidden="true" />
              {placePath(state, location.id)}
            </div>
          )}
        </CardBody>
      </Card>
      {location && (
        <>
          <div className="flex gap-2">
            <Counter
              label={t('store.count.seen')}
              value={cmp.seen.length}
              total={cmp.expected.length}
              tone={cmp.matches && draft.codes.length > 0 ? 'ok' : 'neutral'}
            />
            {cmp.extra.length + cmp.unknown.length > 0 && (
              <Counter
                label={t('store.count.extra')}
                value={cmp.extra.length + cmp.unknown.length}
                tone="warn"
              />
            )}
          </div>
          <ScanButton
            label={<BilingualLabel k="store.count.scanTag" />}
            validate={(c) => (draft.codes.includes(c) ? refusal('DUPLICATE') : null)}
            onScan={(c) =>
              update((d) => (d.codes.includes(c) ? d : { ...d, codes: [...d.codes, c] }))
            }
          />
          <Card>
            <CardHeader eyebrow={t('store.lots')} />
            <CardBody className="py-1">
              <ul>
                {cmp.byLot.map((l) => (
                  <li
                    key={l.unit_id}
                    className="flex items-center justify-between gap-2 border-b border-rule/70 py-2 last:border-b-0"
                  >
                    <span className="font-mono text-[14px]">
                      {state.lots.get(l.unit_id)?.code ?? '—'}
                    </span>
                    <span className="flex items-center gap-2">
                      <span className="font-mono">
                        {l.seen} / {l.expected}
                      </span>
                      {l.short > 0 && draft.codes.length > 0 && (
                        <Badge tone="warn">{t('store.count.shortBy', { n: l.short })}</Badge>
                      )}
                    </span>
                  </li>
                ))}
                {cmp.byLot.length === 0 && (
                  <li className="py-3 text-center text-muted">
                    {t('store.count.nothingExpected')}
                  </li>
                )}
              </ul>
            </CardBody>
          </Card>
          {cmp.extra.length > 0 && (
            <Banner tone="warn" title={t('store.count.extraTitle')}>
              {cmp.extra
                .map((b) => `${b.qr_payload} (${state.lots.get(b.unit_id)?.code ?? '—'})`)
                .join(', ')}
            </Banner>
          )}
          {cmp.unknown.length > 0 && (
            <Banner tone="warn" title={t('store.count.unknownTitle')}>
              {cmp.unknown.join(', ')}
            </Banner>
          )}
          <Button size="xl" variant="accent" fullWidth loading={saving} onClick={() => void done()}>
            {doneLabel ?? <BilingualLabel k="store.count.done" />}
          </Button>
        </>
      )}
    </div>
  )
}
